import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  #mutationObserver = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);

    // Attach free shipping banner close listener
    this.#attachBannerCloseListener();

    // Watch for DOM changes and re-attach listener
    this.#mutationObserver = new MutationObserver(() => {
      this.#attachBannerCloseListener();
    });

    this.#mutationObserver.observe(this, {
      childList: true,
      subtree: true,
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);

    if (this.#mutationObserver) {
      this.#mutationObserver.disconnect();
      this.#mutationObserver = null;
    }
  }

  #attachBannerCloseListener() {
    const closeButton = this.querySelector('.free-shipping-banner__close');
    if (closeButton) {
      // Remove old listener to avoid duplicates
      closeButton.removeEventListener('click', this.#handleBannerClose);
      // Add new listener
      closeButton.addEventListener('click', this.#handleBannerClose);
    }
  }

  #handleBannerClose = (event) => {
    const banner = event.currentTarget.closest('.free-shipping-banner');
    if (banner) {
      banner.style.display = 'none';
    }
  };

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector(
        'shopify-payment-terms'
      )?.shadowRoot;
      const cta = installmentsContent?.querySelector(
        '#shopify-installments-cta'
      );
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
